/**
 * Mint Activity Tracking Utility
 * 
 * Tracks mint events in the nft_activity table so pieces show activity
 * immediately after being minted.
 */

import { supabase } from '../lib/supabase';
import { Connection } from '@solana/web3.js';
import { getRpcEndpoint } from '../config/rpc';

/**
 * Get the current Solana slot and timestamp
 * @param {string} network - Network to use ('devnet' or 'mainnet-beta')
 * @returns {Promise<{slot: number, timestamp: number}>}
 */
async function getCurrentSlotAndTimestamp(network = 'devnet') {
  try {
    // Use Helius RPC instead of public RPC to avoid rate limits
    const rpcEndpoint = getRpcEndpoint({ cluster: network });
    console.log('🔍 Mint tracking using RPC:', rpcEndpoint.includes('helius') ? 'Helius (reliable)' : 'Public (rate-limited)');
    
    const connection = new Connection(rpcEndpoint, 'confirmed');
    
    const slot = await connection.getSlot();
    const blockTime = await connection.getBlockTime(slot);
    
    return {
      slot,
      timestamp: blockTime || Math.floor(Date.now() / 1000)
    };
  } catch (error) {
    console.error('❌ Error getting slot/timestamp:', error);
    // Fallback to current time
    return {
      slot: 0,
      timestamp: Math.floor(Date.now() / 1000)
    };
  }
}

/**
 * Track a mint event in the nft_activity table
 * 
 * @param {Object} params
 * @param {string} params.mintAddress - The NFT mint address
 * @param {string} params.toWallet - The wallet that received the minted NFT
 * @param {string} params.signature - The mint transaction signature
 * @param {string} params.network - Network ('devnet' or 'mainnet-beta')
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function trackMintActivity({ mintAddress, toWallet, signature, network = 'devnet' }) {
  try {
    console.log('📝 Tracking mint activity:', {
      mintAddress: mintAddress.substring(0, 15) + '...',
      toWallet: toWallet.substring(0, 15) + '...',
      signature: signature.substring(0, 15) + '...'
    });

    // Check if mint activity already exists to prevent duplicates
    const { data: existingActivity, error: checkError } = await supabase
      .from('nft_activity')
      .select('id')
      .eq('mint_address', mintAddress)
      .eq('event_type', 'mint')
      .maybeSingle();

    if (checkError) {
      console.warn('⚠️ Error checking for existing mint activity:', checkError.message);
    }

    if (existingActivity) {
      console.log('ℹ️ Mint activity already tracked for this mint address');
      return { success: true, duplicate: true };
    }

    // Get current slot and timestamp from Solana
    const { slot, timestamp } = await getCurrentSlotAndTimestamp(network);
    const now = new Date();

    // Fetch the piece to get its mint price
    let mintPrice = 0;
    let mintPriceLamports = 0;
    const { data: pieceData } = await supabase
      .from('pieces')
      .select('listing_price, price')
      .eq('mint_address', mintAddress)
      .maybeSingle();
    
    if (pieceData) {
      mintPrice = pieceData.listing_price || pieceData.price || 0;
      mintPriceLamports = Math.floor(mintPrice * 1e9); // Always calculate, even if 0
      console.log('💰 Mint price for activity tracking:', {
        mint_address: mintAddress.substring(0, 15) + '...',
        listing_price: pieceData.listing_price,
        price: pieceData.price,
        final_mint_price: mintPrice
      });
    }

    // Insert activity record with updated schema
    const { data, error } = await supabase
      .from('nft_activity')
      .insert({
        mint_address: mintAddress,
        event_type: 'mint',
        from_address: null, // Mints don't have a "from" address
        to_address: toWallet,
        price: mintPrice,
        amount_lamports: mintPriceLamports,
        slot: slot,
        event_timestamp: timestamp,
        signature: signature,
        tx_sig: signature,
        block_time: now.toISOString(),
        created_at: now.toISOString(),
        source: 'app'
      })
      .select()
      .maybeSingle();

    if (error) {
      // Check if it's a duplicate error (unique constraint violation)
      if (error.code === '23505') {
        console.log('ℹ️ Mint activity already tracked (duplicate signature)');
        return { success: true, duplicate: true };
      }
      
      console.error('❌ Error inserting mint activity:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ Mint activity tracked:', data.id);
    return { success: true, activityId: data.id };

  } catch (error) {
    console.error('❌ Exception tracking mint activity:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Track multiple mint events in bulk (for capsule minting)
 * 
 * @param {Array<Object>} mints - Array of mint objects
 * @param {string} mints[].mintAddress - The NFT mint address
 * @param {string} mints[].toWallet - The wallet that received the minted NFT
 * @param {string} mints[].signature - The mint transaction signature
 * @param {string} network - Network ('devnet' or 'mainnet-beta')
 * @returns {Promise<{successful: number, failed: number, results: Array}>}
 */
export async function trackBulkMintActivity(mints, network = 'devnet') {
  console.log(`📝 Tracking ${mints.length} mint activities...`);
  
  const results = {
    successful: 0,
    failed: 0,
    results: []
  };

  // Get current slot and timestamp once for all mints
  const { slot, timestamp } = await getCurrentSlotAndTimestamp(network);
  const now = new Date();

  // Prepare bulk insert data with updated schema
  const activityRecords = mints.map(mint => ({
    mint_address: mint.mintAddress,
    event_type: 'mint',
    from_address: null,
    to_address: mint.toWallet,
    price: null,
    amount_lamports: null,
    slot: slot,
    event_timestamp: timestamp,
    signature: mint.signature,
    tx_sig: mint.signature,
    block_time: now.toISOString(),
    created_at: now.toISOString(),
    source: 'app'
  }));

  try {
    const { data, error } = await supabase
      .from('nft_activity')
      .insert(activityRecords)
      .select();

    if (error) {
      console.error('❌ Error bulk inserting mint activities:', error);
      results.failed = mints.length;
      results.results = mints.map((mint, i) => ({
        index: i,
        mintAddress: mint.mintAddress,
        success: false,
        error: error.message
      }));
      return results;
    }

    console.log(`✅ Tracked ${data.length} mint activities`);
    results.successful = data.length;
    results.results = data.map((activity, i) => ({
      index: i,
      mintAddress: activity.mint_address,
      success: true,
      activityId: activity.id
    }));

  } catch (error) {
    console.error('❌ Exception tracking bulk mint activities:', error);
    results.failed = mints.length;
    results.results = mints.map((mint, i) => ({
      index: i,
      mintAddress: mint.mintAddress,
      success: false,
      error: error.message
    }));
  }

  return results;
}

